// Client mirrors server/document-handlers/documentRoles.ts — keep token lists in sync.

import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { AuthContextValue } from "@/contexts/AuthContext";

export const DOC_VIEWER_ROLES = [
  "sales_rep",
  "sales",
  "trainer",
  "marketing",
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  "dev",
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
] as const;

export const DOC_EDITOR_ROLES = [
  "trainer",
  "marketing",
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  "dev",
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
] as const;

export const DOC_PUBLISHER_ROLES = [
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  "dev",
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
] as const;

export const DOC_ADMINISTRATOR_ROLES = [
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  "dev",
  "platform_owner",
  "platform_admin",
  "platform_dev",
] as const;

type RoleAuth = Pick<AuthContextValue, "session" | "hasRole"> | null | undefined;

function hasDocRole(auth: RoleAuth, roles: readonly string[]): boolean {
  if (!auth?.session) return false;
  return roles.some((role) => auth.hasRole(role));
}

/** Library browse + fill + export (mode gate is the primary entry check). */
export function canViewDocuments(auth: AuthContextValue | null | undefined): boolean {
  if (!auth) return false;
  return canAccessPrimaryMode("formsAndFormats", auth);
}

/** Create/edit drafts and save versions. */
export function canEditDocuments(auth: RoleAuth): boolean {
  return hasDocRole(auth, DOC_EDITOR_ROLES);
}

/** Publish drafts and restore published versions to a new draft. */
export function canPublishDocuments(auth: RoleAuth): boolean {
  return hasDocRole(auth, DOC_PUBLISHER_ROLES);
}

export function canRestoreDocuments(auth: RoleAuth): boolean {
  return canPublishDocuments(auth);
}

/** Manage categories, assets, and org-wide document settings. */
export function canAdminDocuments(auth: RoleAuth): boolean {
  return hasDocRole(auth, DOC_ADMINISTRATOR_ROLES);
}
